import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { studentsAPI, reportsAPI, feeTypesAPI, feeTemplatesAPI, hostelFeeTemplatesAPI, transportFeeTemplatesAPI, usersAPI, employeesAPI, settingsAPI, messageTemplatesAPI } from '../services/api';
import { useYear } from '../context/YearContext';
import { TEMPLATE_CATEGORIES } from '../utils/messageTemplates';

const fmt = (n) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);

// ── Bulk Fee Modal ─────────────────────────────────────────────────────────────
function BulkFeeModal({ classes, currentYear, onClose }) {
  const [selectedClass, setSelectedClass] = useState(classes[0] || '');
  const [totalFee,      setTotalFee]      = useState('');
  const [loading,       setLoading]       = useState(false);
  const [result,        setResult]        = useState(null);
  const [error,         setError]         = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!window.confirm(`Set fee ₹${totalFee} for ALL active students in ${selectedClass} (${currentYear})? This will overwrite existing fee values.`)) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await studentsAPI.bulkFee({ class: selectedClass, academic_year: currentYear, total_fee: totalFee });
      setResult(res.data.message);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update fees');
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-box">
        <div className="modal-header">
          <h2>💰 Bulk Fee Assignment</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <p style={{ color: '#555', marginBottom: 16 }}>
              Sets the <strong>Total Fee</strong> for every active student in the selected class for <strong>{currentYear}</strong>.
            </p>
            {error  && <div className="error-banner">{error}</div>}
            {result && <div className="success-banner">✅ {result}</div>}
            <div className="form-grid">
              <div className="form-group">
                <label>Class</label>
                <select value={selectedClass} onChange={(e) => setSelectedClass(e.target.value)} required>
                  {classes.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Total Fee (₹) <span className="required">*</span></label>
                <input
                  type="number"
                  value={totalFee}
                  onChange={(e) => setTotalFee(e.target.value)}
                  required
                  min="0"
                  step="0.01"
                  placeholder="e.g. 12000"
                />
              </div>
            </div>
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Updating…' : 'Apply to All Students'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

// ── Individual Promote Modal ───────────────────────────────────────────────────
function IndividualPromoteModal({ currentYear, onClose }) {
  const nextYear = (() => {
    const parts = currentYear.split('-');
    if (parts.length !== 2) return '';
    const start = parseInt(parts[0], 10);
    return `${start + 1}-${String(start + 2).slice(-2)}`;
  })();

  const [students,         setStudents]         = useState([]);
  const [loadingStudents,  setLoadingStudents]  = useState(true);
  const [search,           setSearch]           = useState('');
  const [selectedStudent,  setSelectedStudent]  = useState(null);
  const [toClass,          setToClass]          = useState('');
  const [toYear,           setToYear]           = useState(nextYear);
  const [promoting,        setPromoting]        = useState(false);
  const [result,           setResult]           = useState(null);
  const [error,            setError]            = useState(null);

  useEffect(() => {
    setLoadingStudents(true);
    studentsAPI.getAll({ year: currentYear })
      .then((r) => setStudents(r.data.data || []))
      .catch(() => setStudents([]))
      .finally(() => setLoadingStudents(false));
  }, [currentYear]);

  const fmtPending = (s) => {
    const gross = parseFloat(s.total_fee || 0) + parseFloat(s.bus_fee || 0) + parseFloat(s.uniform_fee || 0) + parseFloat(s.books_fee || 0) + parseFloat(s.hostel_fee || 0);
    const pending = Math.max(0, gross - parseFloat(s.paid_amount || 0) + parseFloat(s.old_due_amount || 0) - parseFloat(s.concession_amount || 0));
    return fmt(pending);
  };

  const filtered = (() => {
    const q = search.trim().toLowerCase();
    if (!q) return students.slice(0, 30);
    return students.filter((s) =>
      s.student_name.toLowerCase().includes(q) ||
      (s.class || '').toLowerCase().includes(q) ||
      (s.father_name || '').toLowerCase().includes(q) ||
      (s.village || '').toLowerCase().includes(q)
    ).slice(0, 50);
  })();

  const handleSelect = (s) => {
    setSelectedStudent(s);
    const num = parseInt(s.class, 10);
    setToClass(!isNaN(num) ? String(num + 1) : '');
    setError(null);
    setResult(null);
  };

  const handlePromote = async () => {
    if (!selectedStudent || !toClass.trim() || !toYear.trim()) {
      setError('Please select a student and fill in the target class and year.');
      return;
    }
    if (!window.confirm(
      `Promote ${selectedStudent.student_name} (Class ${selectedStudent.class}) → Class ${toClass.trim()} (${toYear.trim()})?\n\nPending dues will carry forward as Old Due and fee fields will reset to 0.`
    )) return;
    setPromoting(true);
    setError(null);
    try {
      const res = await studentsAPI.promoteIndividual({
        student_id: selectedStudent.student_id,
        to_class:   toClass.trim(),
        to_year:    toYear.trim(),
      });
      setResult(res.data.message);
      setSelectedStudent(null);
      setSearch('');
      setToClass('');
      // Refresh student list so promoted student no longer appears in current year
      studentsAPI.getAll({ year: currentYear })
        .then((r) => setStudents(r.data.data || []))
        .catch(() => {});
    } catch (err) {
      setError(err.response?.data?.message || 'Promotion failed');
    } finally {
      setPromoting(false);
    }
  };

  return createPortal(
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-box large">
        <div className="modal-header">
          <h2>👤 Promote Individual Student</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ color: '#555', marginBottom: 16 }}>
            Select a student from <strong>{currentYear}</strong> and promote them to a specific class in the target year.
            Pending dues carry forward as Old Due and fee fields reset to 0.
          </p>
          {error  && <div className="error-banner">{error}</div>}
          {result && <div className="success-banner">✅ {result}</div>}

          {selectedStudent ? (
            <>
              <div style={{ background: '#eff6ff', border: '1.5px solid #3b82f6', borderRadius: 8, padding: '12px 16px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <strong>{selectedStudent.student_name}</strong> — Class <strong>{selectedStudent.class}</strong>
                  {selectedStudent.father_name && <span style={{ color: '#555', marginLeft: 8 }}>({selectedStudent.father_name})</span>}
                  <span style={{ color: '#b91c1c', marginLeft: 8 }}>Pending: {fmtPending(selectedStudent)}</span>
                </div>
                <button className="btn btn-sm btn-secondary" onClick={() => { setSelectedStudent(null); setError(null); setResult(null); }}>
                  Change
                </button>
              </div>
              <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <div className="form-group">
                  <label>New Class <span className="required">*</span></label>
                  <input
                    type="text"
                    value={toClass}
                    onChange={(e) => setToClass(e.target.value)}
                    placeholder="e.g. 6, UKG, Class 2"
                    maxLength={50}
                  />
                </div>
                <div className="form-group">
                  <label>Target Academic Year <span className="required">*</span></label>
                  <input
                    type="text"
                    value={toYear}
                    onChange={(e) => setToYear(e.target.value)}
                    placeholder="e.g. 2026-27"
                    maxLength={10}
                  />
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="form-group" style={{ marginBottom: 12 }}>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by student name, class, father's name, or village…"
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: '0.9rem' }}
                  autoFocus
                />
              </div>
              {loadingStudents ? (
                <div className="loading">Loading students…</div>
              ) : (
                <div className="table-container" style={{ maxHeight: 320, overflowY: 'auto' }}>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Class</th>
                        <th>Father</th>
                        <th>Village</th>
                        <th>Pending</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.length === 0 ? (
                        <tr><td colSpan="6" className="no-data-cell">No students found. Try a different search.</td></tr>
                      ) : (
                        filtered.map((s) => (
                          <tr key={s.student_id}>
                            <td><strong>{s.student_name}</strong></td>
                            <td>{s.class}</td>
                            <td>{s.father_name || '—'}</td>
                            <td>{s.village || '—'}</td>
                            <td style={{ color: '#b91c1c' }}>{fmtPending(s)}</td>
                            <td>
                              <button className="btn btn-sm btn-primary" onClick={() => handleSelect(s)}>Select</button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                  {!search.trim() && students.length > 30 && (
                    <p style={{ color: '#888', fontSize: '0.8rem', padding: '6px 4px' }}>
                      Showing first 30 students. Type to search.
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        <div className="modal-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          {selectedStudent && (
            <button type="button" className="btn btn-danger" onClick={handlePromote} disabled={promoting || !toClass.trim() || !toYear.trim()}>
              {promoting ? 'Promoting…' : '📤 Confirm & Promote'}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Promote Modal ──────────────────────────────────────────────────────────────
function PromoteModal({ classes, currentYear, onClose }) {
  // Auto-generate next year e.g. 2025-26 → 2026-27
  const nextYear = (() => {
    const parts = currentYear.split('-');
    if (parts.length !== 2) return '';
    const start = parseInt(parts[0], 10);
    return `${start + 1}-${String(start + 2).slice(-2)}`;
  })();

  // Build default mappings: try to increment numeric class names
  const buildMappings = () =>
    classes.map((c) => {
      const num = parseInt(c, 10);
      const to  = !isNaN(num) ? String(num + 1) : '';
      return { from: c, to };
    });

  const [toYear,         setToYear]         = useState(nextYear);
  const [mappings,       setMappings]       = useState(buildMappings);
  const [graduateSet,    setGraduateSet]    = useState(new Set());
  const [selectedClass,  setSelectedClass]  = useState('__all__'); // '__all__' or a specific class name
  const [loading,        setLoading]        = useState(false);
  const [result,         setResult]         = useState(null);
  const [error,          setError]          = useState(null);
  const [previewData,    setPreviewData]    = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [feeTemplates,   setFeeTemplates]   = useState({});

  // Load fee templates for toYear whenever toYear changes
  useEffect(() => {
    if (!toYear.trim()) return;
    feeTemplatesAPI.getAll(toYear)
      .then((r) => setFeeTemplates(r.data.data || {}))
      .catch(() => {});
  }, [toYear]);

  const resetPreview = () => setPreviewData(null);

  const toggleGraduate = (cls) => {
    resetPreview();
    setGraduateSet((prev) => {
      const next = new Set(prev);
      if (next.has(cls)) next.delete(cls); else next.add(cls);
      return next;
    });
  };

  const updateTo = (idx, val) => {
    resetPreview();
    setMappings((prev) => prev.map((m, i) => i === idx ? { ...m, to: val } : m));
  };

  // Derive which mappings are "active" based on class selector
  const activeMappings = selectedClass === '__all__'
    ? mappings
    : mappings.filter((m) => m.from === selectedClass);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validMappings = activeMappings.filter((m) => m.to.trim());
    if (validMappings.length === 0)
      return setError('Please set at least one target class before promoting.');
    if (!toYear.trim())
      return setError('Please enter the target academic year.');
    const classLabel = selectedClass === '__all__' ? 'all classes' : `class ${selectedClass}`;
    if (!window.confirm(
      `Promote students (${classLabel}) from ${currentYear} → ${toYear}?\n` +
      `This will:\n• Update class names\n• Move pending dues to Old Due\n• Reset Paid Amount and Total Fee to 0\n` +
      (graduateSet.size > 0 ? `• Graduate (archive) classes: ${[...graduateSet].join(', ')}\n` : '') +
      `\nThis cannot be undone!`
    )) return;

    setLoading(true);
    setError(null);
    setResult(null);
    try {
      // Send all fee_templates as-is (both old-style "ClassName" and new-style "ClassName|FeeType").
      // The backend promote endpoint applies the correct template per fee_type group per class.
      const fee_templates = { ...feeTemplates };
      const res = await studentsAPI.promote({
        from_year: currentYear,
        to_year: toYear,
        class_mappings: validMappings,
        graduate_classes: selectedClass === '__all__' ? [...graduateSet] : [],
        fee_templates,
      });
      setResult(res.data.message);
    } catch (err) {
      setError(err.response?.data?.message || 'Promote failed');
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-box large">
        <div className="modal-header">
          <h2>📤 Promote Students to Next Class</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <p style={{ color: '#555', marginBottom: 16 }}>
              Moves all active students to their next class. Pending dues are carried forward as <em>Old Due</em> and Paid Amount is reset to 0.
            </p>
            {error  && <div className="error-banner">{error}</div>}
            {result && <div className="success-banner">✅ {result}</div>}

            <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr 1fr', marginBottom: 20 }}>
              <div className="form-group">
                <label>From Academic Year</label>
                <input type="text" value={currentYear} readOnly style={{ background: '#f5f5f5' }} />
              </div>
              <div className="form-group">
                <label>To Academic Year <span className="required">*</span></label>
                <input
                  type="text"
                  value={toYear}
                  onChange={(e) => { setToYear(e.target.value); resetPreview(); }}
                  placeholder="e.g. 2026-27"
                  required
                  maxLength={10}
                />
              </div>
              <div className="form-group">
                <label>Promote Which Class</label>
                <select
                  value={selectedClass}
                  onChange={(e) => { setSelectedClass(e.target.value); resetPreview(); }}
                  style={{ padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6, width: '100%' }}
                >
                  <option value="__all__">All Classes</option>
                  {classes.map((c) => (
                    <option key={c} value={c}>Class {c} only</option>
                  ))}
                </select>
              </div>
            </div>

            <h3 style={{ marginBottom: 8, fontSize: '0.95rem' }}>Class Mapping{selectedClass !== '__all__' ? ` — Class ${selectedClass}` : ''}</h3>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Current Class ({currentYear})</th>
                    <th>New Class ({toYear || '…'})</th>
                    {selectedClass === '__all__' && (
                      <th title="Graduating classes (e.g. Class 10) will be archived instead of promoted">🎓 Graduate</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {activeMappings.map((m, relIdx) => {
                    const globalIdx = mappings.indexOf(m);
                    return (
                      <tr key={m.from} style={graduateSet.has(m.from) ? { background: '#fef3c7' } : {}}>
                        <td><strong>{m.from}</strong></td>
                        <td>
                          <input
                            type="text"
                            value={m.to}
                            disabled={graduateSet.has(m.from)}
                            onChange={(e) => updateTo(globalIdx, e.target.value)}
                            placeholder={graduateSet.has(m.from) ? 'Will be archived' : 'New class name (blank = skip)'}
                            style={{ width: '100%', padding: '4px 8px', border: '1px solid #ddd', borderRadius: 4, background: graduateSet.has(m.from) ? '#fef3c7' : '' }}
                            maxLength={50}
                          />
                        </td>
                        {selectedClass === '__all__' && (
                          <td style={{ textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={graduateSet.has(m.from)}
                              onChange={() => toggleGraduate(m.from)}
                              title="Archive these students (graduated)"
                              style={{ width: 18, height: 18, cursor: 'pointer' }}
                            />
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* ── Preview section ── */}
            {previewLoading && <div className="loading" style={{ marginTop: 12 }}>Loading preview…</div>}
            {previewData && (
              <div style={{ marginTop: 20 }}>
                <h3 style={{ marginBottom: 8, fontSize: '0.95rem', color: '#0f172a' }}>Preview: What will happen</h3>
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Current Class</th>
                        <th>New Class</th>
                        <th>Students</th>
                        <th>Dues to Carry Forward</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.length === 0 ? (
                        <tr><td colSpan="4" className="no-data-cell">No active students found for the selected classes.</td></tr>
                      ) : (
                        previewData.map((row) => (
                          <tr key={row.from} style={row.isGrad ? { background: '#fef3c7' } : {}}>
                            <td><strong>{row.from}</strong></td>
                            <td><strong className={row.isGrad ? 'text-orange' : 'text-green'}>{row.to}</strong></td>
                            <td>{row.students}</td>
                            <td className={row.isGrad ? '' : (parseFloat(row.dues) > 0 ? 'text-orange' : 'text-green')}>
                              {row.isGrad ? '—' : new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(row.dues || 0)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                    {previewData.length > 0 && (
                      <tfoot>
                        <tr>
                          <td colSpan="2"><strong>Total</strong></td>
                          <td><strong>{previewData.reduce((s, r) => s + r.students, 0)}</strong></td>
                          <td className="text-orange">
                            <strong>{new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(previewData.reduce((s, r) => s + parseFloat(r.dues || 0), 0))}</strong>
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
                <p style={{ color: '#c2410c', fontSize: '0.83rem', marginTop: 8 }}>
                  ⚠️ Pending dues will move to <strong>Old Due</strong> and <strong>Paid Amount</strong> will reset to 0. This cannot be undone.
                </p>
              </div>
            )}
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button
              type="button"
              className="btn btn-info"
              disabled={previewLoading}
              onClick={async () => {
                const validMappings = activeMappings.filter((m) => m.to.trim());
                if (validMappings.length === 0) return setError('Please set at least one target class.');
                if (!toYear.trim()) return setError('Please enter the target academic year.');
                setPreviewLoading(true);
                setError(null);
                setPreviewData(null);
                try {
                  const res = await reportsAPI.getClassReport(currentYear);
                  const classStats = res.data.data;
                  const preview = validMappings.map((m) => {
                    const stats = classStats.find((c) => c.class === m.from) || { total_students: 0, total_pending: 0 };
                    const isGrad = graduateSet.has(m.from);
                    return { from: m.from, to: isGrad ? '🎓 Graduate' : m.to.trim(), students: stats.total_students, dues: stats.total_pending, isGrad };
                  });
                  // Add graduating classes that aren't in validMappings (only if showing all)
                  if (selectedClass === '__all__') {
                    for (const cls of graduateSet) {
                      if (!preview.find((p) => p.from === cls)) {
                        const stats = classStats.find((c) => c.class === cls) || { total_students: 0, total_pending: 0 };
                        preview.push({ from: cls, to: '🎓 Graduate', students: stats.total_students, dues: stats.total_pending, isGrad: true });
                      }
                    }
                  }
                  setPreviewData(preview);
                } catch {
                  setError('Failed to load preview data.');
                } finally {
                  setPreviewLoading(false);
                }
              }}
            >
              {previewLoading ? 'Loading…' : '📋 Preview Changes'}
            </button>
            <button type="submit" className="btn btn-danger" disabled={loading || !previewData}>
              {loading ? 'Promoting…' : '📤 Confirm & Promote'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

// ── Fee Templates ──────────────────────────────────────────────────────────────
const FEE_TYPES = ['Regular', 'Olympiad', 'IIT'];
const emptyRow = () => ({ term1: '', term2: '', term3: '', books: '', uniform: '' });

const YEAR_OPTIONS = ['2022-23','2023-24','2024-25','2025-26','2026-27','2027-28','2028-29','2029-30','2030-31','2031-32','2032-33','2033-34'];

// Encode/decode the composite key stored in the DB class column
const makeClassKey  = (cls, feeType) => `${cls.trim()}|${feeType}`;
const parseClassKey = (key) => {
  const idx = key.lastIndexOf('|');
  if (idx === -1) return { cls: key, feeType: 'Regular' };
  return { cls: key.slice(0, idx), feeType: key.slice(idx + 1) };
};

function FeeTemplatesSection() {
  const { year: currentGlobalYear } = useYear();
  const selectedYear = currentGlobalYear || '2025-26';
  const [selectedFeeType, setSelectedFeeType] = useState('Regular');
  const [unlocked,  setUnlocked]  = useState(false);
  const [pwInput,   setPwInput]   = useState('');
  const [pwError,   setPwError]   = useState(false);
  const [templates, setTemplates] = useState({});
  const [cls,       setCls]       = useState('');
  const [row,       setRow]       = useState(emptyRow());
  const [saving,    setSaving]    = useState(false);
  const [saved,     setSaved]     = useState(false);
  const [loadError,        setLoadError]        = useState(null);
  const feeImportRef                             = useRef(null);
  const [feeImportLoading, setFeeImportLoading] = useState(false);
  const [feeImportResult,  setFeeImportResult]  = useState(null);
  // Reset password state
  const [showResetPw,   setShowResetPw]   = useState(false);
  const [currentPw,     setCurrentPw]     = useState('');
  const [newPw,         setNewPw]         = useState('');
  const [confirmPw,     setConfirmPw]     = useState('');
  const [resetPwError,  setResetPwError]  = useState('');
  const [resetPwOk,     setResetPwOk]     = useState('');
  const [resetPwSaving, setResetPwSaving] = useState(false);
  // Forgot password state
  const [showForgotPw,    setShowForgotPw]    = useState(false);
  const [forgotLoginPw,   setForgotLoginPw]   = useState('');
  const [forgotNewPw,     setForgotNewPw]     = useState('');
  const [forgotConfirmPw, setForgotConfirmPw] = useState('');
  const [forgotPwError,   setForgotPwError]   = useState('');
  const [forgotPwOk,      setForgotPwOk]      = useState('');
  const [forgotPwSaving,  setForgotPwSaving]  = useState(false);

  // Load from API whenever selected year changes
  useEffect(() => {
    setLoadError(null);
    feeTemplatesAPI.getAll(selectedYear)
      .then((r) => setTemplates(r.data.data || {}))
      .catch(() => setLoadError('Failed to load fee templates'));
  }, [selectedYear]);

  // Filter templates to show only those matching selectedFeeType
  // For new-style keys "Class|FeeType", match fee type. For old-style keys (no |), show under Regular.
  const filteredEntries = Object.entries(templates)
    .filter(([key]) => {
      const { feeType } = parseClassKey(key);
      return feeType === selectedFeeType;
    })
    .map(([key, f]) => {
      const { cls: className } = parseClassKey(key);
      return [className, key, f];
    })
    .sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true }));

  const handleUnlock = async (e) => {
    e.preventDefault();
    try {
      await feeTemplatesAPI.verifyPassword(pwInput);
      setUnlocked(true);
      setPwError(false);
      setPwInput('');
    } catch (err) {
      setPwError(true);
      setPwInput('');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetPwError('');
    setResetPwOk('');
    if (newPw.length < 8) { setResetPwError('New password must be at least 8 characters'); return; }
    if (newPw !== confirmPw) { setResetPwError('New passwords do not match'); return; }
    if (newPw === currentPw) { setResetPwError('New password must be different from current password'); return; }
    setResetPwSaving(true);
    try {
      await feeTemplatesAPI.changePassword(currentPw, newPw);
      setResetPwOk('Password changed successfully!');
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
      setTimeout(() => { setShowResetPw(false); setResetPwOk(''); }, 1500);
    } catch (err) {
      setResetPwError(err.response?.data?.message || 'Failed to change password');
    } finally { setResetPwSaving(false); }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotPwError('');
    setForgotPwOk('');
    if (forgotNewPw.length < 8) { setForgotPwError('New password must be at least 8 characters'); return; }
    if (forgotNewPw !== forgotConfirmPw) { setForgotPwError('New passwords do not match'); return; }
    setForgotPwSaving(true);
    try {
      await feeTemplatesAPI.forgotPassword(forgotLoginPw, forgotNewPw);
      setForgotPwOk('Fee template password has been reset!');
      setForgotLoginPw(''); setForgotNewPw(''); setForgotConfirmPw('');
      setTimeout(() => { setShowForgotPw(false); setForgotPwOk(''); }, 1500);
    } catch (err) {
      setForgotPwError(err.response?.data?.message || 'Failed to reset password');
    } finally { setForgotPwSaving(false); }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!cls.trim()) return;
    setSaving(true);
    try {
      // term1→tuition, term2→bus, term3→term3, uniform→uniform, books→books in DB
      const data = {
        tuition: parseFloat(row.term1)   || 0,
        bus:     parseFloat(row.term2)   || 0,
        term3:   parseFloat(row.term3)   || 0,
        uniform: parseFloat(row.uniform) || 0,
        books:   parseFloat(row.books)   || 0,
      };
      const dbKey = makeClassKey(cls, selectedFeeType);
      await feeTemplatesAPI.upsert(dbKey, selectedYear, data);
      setTemplates((prev) => ({ ...prev, [dbKey]: data }));
      setCls('');
      setRow(emptyRow());
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    } catch {
      alert('Failed to save template. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (dbKey) => {
    try {
      await feeTemplatesAPI.delete(dbKey, selectedYear);
      setTemplates((prev) => {
        const next = { ...prev };
        delete next[dbKey];
        return next;
      });
    } catch {
      alert('Failed to delete template. Please try again.');
    }
  };

  const handleFeeImportCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    setFeeImportResult(null);
    setFeeImportLoading(true);
    try {
      const text = await file.text();
      const lines = text.trim().split('\n').filter(Boolean);
      const dataLines = lines[0]?.toLowerCase().includes('class') ? lines.slice(1) : lines;
      let success = 0;
      const errors = [];
      for (const line of dataLines) {
        const parts = line.split(',').map((s) => s.trim().replace(/^"|"$/g, ''));
        const [clsName, t1, t2, t3, books, uniform] = parts;
        if (!clsName) continue;
        try {
          const data = {
            tuition: parseFloat(t1)      || 0,
            bus:     parseFloat(t2)      || 0,
            term3:   parseFloat(t3)      || 0,
            books:   parseFloat(books)   || 0,
            uniform: parseFloat(uniform) || 0,
          };
          const dbKey = makeClassKey(clsName, selectedFeeType);
          await feeTemplatesAPI.upsert(dbKey, selectedYear, data);
          setTemplates((prev) => ({ ...prev, [dbKey]: data }));
          success++;
        } catch (err) {
          errors.push(`Class "${clsName}": ${err.response?.data?.message || 'failed'}`);
        }
      }
      setFeeImportResult({ success, errors });
    } catch {
      setFeeImportResult({ success: 0, errors: ['Failed to read file'] });
    } finally {
      setFeeImportLoading(false);
    }
  };

  const inp = (ph, key, w = 100) => (
    <input
      type="number" value={row[key]} placeholder={ph}
      onChange={(e) => setRow((p) => ({ ...p, [key]: e.target.value }))}
      style={{ padding: '6px 8px', border: '1px solid #ddd', borderRadius: 6, width: w }}
      min="0" step="1"
    />
  );

  const feeTypeColors = { Regular: '#eff6ff', Olympiad: '#f0fdf4', IIT: '#fef9c3' };
  const feeTypeBorders = { Regular: '#bfdbfe', Olympiad: '#bbf7d0', IIT: '#fde68a' };
  const feeTypeTextColors = { Regular: '#1d4ed8', Olympiad: '#15803d', IIT: '#92400e' };

  return (
    <div className="fee-templates-section">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 700, margin: 0 }}>📋 Fee Templates</h2>
        {/* Year display — change from Dashboard only */}
        <span style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: 6, fontSize: '0.85rem', background: '#f9fafb', color: '#374151', fontWeight: 600 }}>
          {selectedYear}
        </span>
        <button
          className="lock-toggle-btn"
          title={unlocked ? 'Click to lock' : 'Click to unlock and edit'}
          onClick={() => unlocked ? setUnlocked(false) : undefined}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', padding: 0 }}
        >{unlocked ? '🔓' : '🔒'}</button>
      </div>
      <p style={{ color: '#666', fontSize: '0.85rem', marginBottom: 14 }}>
        Set default fees per class for <strong>{selectedYear}</strong>. These auto-fill when adding a new student and are applied on promotion to the next class.
      </p>

      {/* Fee Type tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
        {FEE_TYPES.map((ft) => (
          <button
            key={ft}
            type="button"
            onClick={() => setSelectedFeeType(ft)}
            style={{
              padding: '5px 16px',
              borderRadius: 20,
              border: `1.5px solid ${selectedFeeType === ft ? feeTypeBorders[ft] : '#e5e7eb'}`,
              background: selectedFeeType === ft ? feeTypeColors[ft] : '#f9fafb',
              color: selectedFeeType === ft ? feeTypeTextColors[ft] : '#6b7280',
              fontWeight: selectedFeeType === ft ? 700 : 400,
              fontSize: '0.85rem',
              cursor: 'pointer',
              transition: 'all 0.15s',
            }}
          >
            {ft}
          </button>
        ))}
      </div>

      {loadError && <p style={{ color: '#dc2626', fontSize: '0.85rem' }}>{loadError}</p>}

      {!unlocked && (
        <form onSubmit={handleUnlock} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="password" value={pwInput}
            onChange={(e) => { setPwInput(e.target.value); setPwError(false); }}
            placeholder="Enter fee template password"
            style={{ padding: '6px 10px', border: `1px solid ${pwError ? '#f87171' : '#ddd'}`, borderRadius: 6, width: 200 }}
            autoFocus
          />
          <button type="submit" className="btn btn-primary btn-sm">🔓 Unlock</button>
          {pwError && <span style={{ color: '#dc2626', fontSize: '0.85rem' }}>❌ Wrong password</span>}
        </form>
      )}
      {!unlocked && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
          <p style={{ color: '#6b7280', fontSize: '0.85rem', margin: 0 }}>
            Default password: <strong>Admin</strong>
          </p>
          <button
            type="button"
            className="btn btn-sm"
            style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d', fontSize: '0.8rem', padding: '3px 10px' }}
            onClick={() => { setShowResetPw((v) => !v); setResetPwError(''); setResetPwOk(''); setCurrentPw(''); setNewPw(''); setConfirmPw(''); }}
          >
            🔑 {showResetPw ? 'Cancel' : 'Reset Password'}
          </button>
        </div>
      )}

      {!unlocked && showResetPw && (
        <form onSubmit={handleResetPassword} style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 10, padding: '14px 18px', marginTop: 10, maxWidth: 400 }}>
          <div style={{ fontWeight: 600, marginBottom: 10, fontSize: '0.9rem' }}>🔑 Change Fee Template Password</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <input type="password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)}
              placeholder="Current password" required
              style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.9rem' }} />
            <input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)}
              placeholder="New password (min 8 characters)" required
              style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.9rem' }} />
            <input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)}
              placeholder="Confirm new password" required
              style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.9rem' }} />
          </div>
          {resetPwError && <div style={{ color: '#dc2626', fontSize: '0.85rem', marginTop: 8 }}>{resetPwError}</div>}
          {resetPwOk   && <div style={{ color: '#166534', fontSize: '0.85rem', marginTop: 8 }}>✅ {resetPwOk}</div>}
          <button type="submit" className="btn btn-primary btn-sm" style={{ marginTop: 10 }} disabled={resetPwSaving}>
            {resetPwSaving ? 'Saving…' : '💾 Save New Password'}
          </button>
        </form>
      )}

      {!unlocked && !showResetPw && !showForgotPw && (
        <button
          type="button"
          onClick={() => { setShowForgotPw(true); setForgotPwError(''); setForgotPwOk(''); setForgotLoginPw(''); setForgotNewPw(''); setForgotConfirmPw(''); }}
          style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: '0.83rem', marginTop: 6, textDecoration: 'underline', padding: 0 }}
        >
          Forgot fee template password?
        </button>
      )}

      {!unlocked && showForgotPw && (
        <form onSubmit={handleForgotPassword} style={{ background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 10, padding: '14px 18px', marginTop: 10, maxWidth: 400 }}>
          <div style={{ fontWeight: 600, marginBottom: 6, fontSize: '0.9rem' }}>🔓 Reset via School Login Password</div>
          <p style={{ color: '#6b7280', fontSize: '0.82rem', marginBottom: 10 }}>
            Verify your <strong>school login password</strong> to set a new fee template password.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <input type="password" value={forgotLoginPw} onChange={(e) => setForgotLoginPw(e.target.value)}
              placeholder="Your school login password" required
              style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.9rem' }} />
            <input type="password" value={forgotNewPw} onChange={(e) => setForgotNewPw(e.target.value)}
              placeholder="New fee template password (min 8 chars)" required
              style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.9rem' }} />
            <input type="password" value={forgotConfirmPw} onChange={(e) => setForgotConfirmPw(e.target.value)}
              placeholder="Confirm new password" required
              style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.9rem' }} />
          </div>
          {forgotPwError && <div style={{ color: '#dc2626', fontSize: '0.85rem', marginTop: 8 }}>{forgotPwError}</div>}
          {forgotPwOk   && <div style={{ color: '#166534', fontSize: '0.85rem', marginTop: 8 }}>✅ {forgotPwOk}</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button type="submit" className="btn btn-primary btn-sm" disabled={forgotPwSaving}>
              {forgotPwSaving ? 'Saving…' : '🔓 Reset Password'}
            </button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowForgotPw(false)}>Cancel</button>
          </div>
        </form>
      )}

      {unlocked && (
        <>
          {/* Add / update row */}
          <div style={{
            background: feeTypeColors[selectedFeeType],
            border: `1px solid ${feeTypeBorders[selectedFeeType]}`,
            borderRadius: 10,
            padding: '12px 14px',
            marginBottom: 14,
          }}>
            <div style={{ fontSize: '0.82rem', fontWeight: 600, color: feeTypeTextColors[selectedFeeType], marginBottom: 8 }}>
              Add / Update — {selectedFeeType} Fee Structure ({selectedYear})
            </div>
            <form onSubmit={handleAdd} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <input
                type="text" value={cls}
                onChange={(e) => setCls(e.target.value)}
                placeholder="Class (e.g. 6)"
                style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, width: 110 }}
                maxLength={20} required
              />
              {inp('Term 1 (₹)',  'term1',   100)}
              {inp('Term 2 (₹)',  'term2',   100)}
              {inp('Term 3 (₹)',  'term3',   100)}
              {inp('Books (₹)',   'books',    90)}
              {inp('Uniform (₹)', 'uniform',  95)}
              <div style={{ display: 'flex', alignItems: 'center', padding: '6px 10px', background: '#fff', borderRadius: 6, border: '1px solid #e5e7eb', fontSize: '0.82rem', color: '#374151', minWidth: 120 }}>
                Total: ₹{(
                  (parseFloat(row.term1)  ||0) +
                  (parseFloat(row.term2)  ||0) +
                  (parseFloat(row.term3)  ||0) +
                  (parseFloat(row.books)  ||0) +
                  (parseFloat(row.uniform)||0)
                ).toLocaleString('en-IN')}
              </div>
              <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
                {saving ? 'Saving…' : '+ Set'}
              </button>
              {saved && <span style={{ color: '#16a34a', alignSelf: 'center', fontSize: '0.85rem' }}>✅ Saved</span>}
            </form>
          </div>

          {/* Import row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
            <input type="file" ref={feeImportRef} accept=".csv" style={{ display: 'none' }} onChange={handleFeeImportCSV} />
            <button
              type="button"
              className="btn btn-sm"
              style={{ background: feeTypeColors[selectedFeeType], color: feeTypeTextColors[selectedFeeType], border: `1px solid ${feeTypeBorders[selectedFeeType]}` }}
              onClick={() => { setFeeImportResult(null); feeImportRef.current?.click(); }}
              disabled={feeImportLoading}
            >
              {feeImportLoading ? 'Importing…' : `📥 Import ${selectedFeeType} CSV`}
            </button>
            <a
              href="data:text/csv;charset=utf-8,class%2Cterm1%2Cterm2%2Cterm3%2Cbooks%2Cuniform"
              download={`${selectedFeeType.toLowerCase()}_fee_template.csv`}
              style={{ fontSize: '0.8rem', color: '#6b7280', textDecoration: 'underline' }}
            >
              Download template
            </a>
            {feeImportResult && (
              <div style={{ padding: '5px 10px', borderRadius: 6, background: feeImportResult.errors.length === 0 ? '#f0fdf4' : '#fff7ed', border: `1px solid ${feeImportResult.errors.length === 0 ? '#bbf7d0' : '#fed7aa'}`, fontSize: '0.83rem' }}>
                {feeImportResult.success > 0 && <span style={{ color: '#166534' }}>✅ Imported {feeImportResult.success} row{feeImportResult.success !== 1 ? 's' : ''}. </span>}
                {feeImportResult.errors.map((err, i) => <span key={i} style={{ color: '#c2410c' }}>❌ {err} </span>)}
              </div>
            )}
          </div>

          {filteredEntries.length === 0 ? (
            <p style={{ color: '#999', fontSize: '0.85rem' }}>No {selectedFeeType} templates set yet for {selectedYear}.</p>
          ) : (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>CLASS</th>
                    <th>TERM 1 (₹)</th>
                    <th>TERM 2 (₹)</th>
                    <th>TERM 3 (₹)</th>
                    <th>TOTAL TUITION</th>
                    <th>BOOKS (₹)</th>
                    <th>UNIFORM (₹)</th>
                    <th>GRAND TOTAL</th>
                    <th>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEntries.map(([className, dbKey, f]) => {
                    const tuition  = f.tuition || 0;
                    const busFld   = f.bus     || 0;
                    const term3Fld = f.term3   || 0;
                    const books    = f.books   || 0;
                    const uniform  = f.uniform || 0;
                    const totalTuition = tuition + busFld + term3Fld;
                    const grandTotal   = totalTuition + books + uniform;
                    return (
                      <tr key={dbKey}>
                        <td><strong>{className}</strong></td>
                        <td>{tuition  > 0 ? `₹${tuition.toLocaleString('en-IN')}`   : '—'}</td>
                        <td>{busFld   > 0 ? `₹${busFld.toLocaleString('en-IN')}`   : '—'}</td>
                        <td>{term3Fld > 0 ? `₹${term3Fld.toLocaleString('en-IN')}` : '—'}</td>
                        <td style={{ fontWeight: 600, color: '#1d4ed8' }}>₹{totalTuition.toLocaleString('en-IN')}</td>
                        <td>{books   > 0 ? `₹${books.toLocaleString('en-IN')}`   : '—'}</td>
                        <td>{uniform > 0 ? `₹${uniform.toLocaleString('en-IN')}` : '—'}</td>
                        <td style={{ fontWeight: 700, color: '#92400e' }}>₹{grandTotal.toLocaleString('en-IN')}</td>
                        <td style={{ display: 'flex', gap: 4 }}>
                          <button
                            className="btn btn-sm btn-secondary"
                            title="Edit template"
                            onClick={() => {
                              setCls(className);
                              setRow({
                                term1:   String(tuition  || ''),
                                term2:   String(busFld   || ''),
                                term3:   String(term3Fld || ''),
                                books:   String(books    || ''),
                                uniform: String(uniform  || ''),
                              });
                              window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                          >✏️</button>
                          <button className="btn btn-sm btn-danger" onClick={() => handleDelete(dbKey)} title="Remove template">🗑️</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Fee Types Section ──────────────────────────────────────────────────────────
function FeeTypesSection() {
  const { year: currentYear } = useYear();
  const [feeTypes,   setFeeTypes]   = useState([]);
  const [newName,    setNewName]    = useState('');
  const [newAmount,  setNewAmount]  = useState('');
  const [editId,     setEditId]     = useState(null);
  const [editName,   setEditName]   = useState('');
  const [editAmount, setEditAmount] = useState('');
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState(null);

  const load = (yr) => {
    feeTypesAPI.getAll(yr).then((r) => setFeeTypes(r.data.data || [])).catch(() => {});
  };

  useEffect(() => { if (currentYear) load(currentYear); }, [currentYear]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setLoading(true);
    setError(null);
    try {
      await feeTypesAPI.create(newName.trim(), parseFloat(newAmount) || 0, currentYear);
      setNewName('');
      setNewAmount('');
      load(currentYear);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create fee type');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (ft) => {
    setEditId(ft.fee_type_id);
    setEditName(ft.fee_name);
    setEditAmount(String(ft.fee_amount || ''));
  };

  const handleEditSave = async (id) => {
    if (!editName.trim()) return;
    setError(null);
    try {
      await feeTypesAPI.update(id, editName.trim(), parseFloat(editAmount) || 0);
      setEditId(null);
      load(currentYear);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update fee type');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this custom fee type?')) return;
    try {
      await feeTypesAPI.delete(id);
      load(currentYear);
    } catch { alert('Failed to remove fee type.'); }
  };

  return (
    <div className="fee-templates-section">
      <h2 style={{ fontSize: '1rem', fontWeight: 700, margin: '0 0 6px' }}>🏷️ Custom Fee Types — {currentYear}</h2>
      <p style={{ color: '#666', fontSize: '0.85rem', marginBottom: 14 }}>
        Create custom fee categories (e.g. Lab Fee, Sports Fee, Exam Fee) that appear in the payment type dropdown for <strong>{currentYear}</strong>.
      </p>
      {error && <div className="error-banner" style={{ marginBottom: 10 }}>{error}</div>}
      <form onSubmit={handleAdd} style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <label style={{ fontSize: '.78rem', color: '#555' }}>Fee Type Name <span style={{ color: 'red' }}>*</span></label>
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="e.g. Lab Fee"
            style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, width: 200 }}
            maxLength={100}
            required
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <label style={{ fontSize: '.78rem', color: '#555' }}>Amount (₹)</label>
          <input
            type="number"
            value={newAmount}
            onChange={(e) => setNewAmount(e.target.value)}
            placeholder="e.g. 500"
            style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, width: 120 }}
            min="0"
            step="0.01"
          />
        </div>
        <button type="submit" className="btn btn-primary btn-sm" disabled={loading} style={{ alignSelf: 'flex-end' }}>
          {loading ? 'Adding…' : '+ Add Fee Type'}
        </button>
      </form>
      {feeTypes.length === 0 ? (
        <p style={{ color: '#999', fontSize: '0.85rem' }}>No custom fee types yet. The built-in types (Transport, Uniform, Books, Other) are always available.</p>
      ) : (
        <div className="table-container">
        <table className="data-table" style={{ maxWidth: 520 }}>
          <thead>
            <tr><th>Fee Type Name</th><th>Amount (₹)</th><th>ACTIONS</th></tr>
          </thead>
          <tbody>
            {feeTypes.map((ft) => (
              <tr key={ft.fee_type_id}>
                <td>
                  {editId === ft.fee_type_id
                    ? <input type="text" value={editName} onChange={(e) => setEditName(e.target.value)} style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: 5, width: 160 }} maxLength={100} />
                    : <strong>{ft.fee_name}</strong>}
                </td>
                <td>
                  {editId === ft.fee_type_id
                    ? <input type="number" value={editAmount} onChange={(e) => setEditAmount(e.target.value)} style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: 5, width: 100 }} min="0" step="0.01" />
                    : ft.fee_amount > 0 ? fmt(ft.fee_amount) : <span style={{ color: '#aaa' }}>—</span>}
                </td>
                <td style={{ display: 'flex', gap: 4 }}>
                  {editId === ft.fee_type_id ? (
                    <>
                      <button className="btn btn-sm btn-primary" onClick={() => handleEditSave(ft.fee_type_id)}>✔ Save</button>
                      <button className="btn btn-sm btn-secondary" onClick={() => setEditId(null)}>✕</button>
                    </>
                  ) : (
                    <>
                      <button className="btn btn-sm btn-secondary" onClick={() => handleEdit(ft)} title="Edit">✏️</button>
                      <button className="btn btn-sm btn-danger" onClick={() => handleDelete(ft.fee_type_id)} title="Remove">🗑️</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
}

// ── Hostel Fee Templates Section ──────────────────────────────────────────────
const HOSTEL_FEE_OPTIONS = ['AC Hostel', 'Non AC Hostel'];

function HostelFeeSection() {
  const { year: currentYear } = useYear();
  const [entries,   setEntries]   = useState([]);
  const [newName,   setNewName]   = useState('');
  const [newTerm1,  setNewTerm1]  = useState('');
  const [newTerm2,  setNewTerm2]  = useState('');
  const [editId,    setEditId]    = useState(null);
  const [editTerm1, setEditTerm1] = useState('');
  const [editTerm2, setEditTerm2] = useState('');
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState(null);
  const hostelImportRef                   = useRef(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult,  setImportResult]  = useState(null);

  const load = (yr) => {
    hostelFeeTemplatesAPI.getAll(yr)
      .then((r) => setEntries(r.data.data || []))
      .catch(() => {});
  };

  useEffect(() => { if (currentYear) load(currentYear); }, [currentYear]);

  const handleHostelImportCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    setImportResult(null);
    setImportLoading(true);
    try {
      const text = await file.text();
      const lines = text.trim().split('\n').filter(Boolean);
      const dataLines = lines[0]?.toLowerCase().includes('fee_name') ? lines.slice(1) : lines;
      let success = 0;
      const errors = [];
      for (const line of dataLines) {
        const parts = line.split(',').map((s) => s.trim().replace(/^"|"$/g, ''));
        const [name, t1, t2] = parts;
        if (!name) continue;
        try {
          await hostelFeeTemplatesAPI.create(currentYear, name, parseFloat(t1) || 0, parseFloat(t2) || 0);
          success++;
        } catch (err) {
          errors.push(`"${name}": ${err.response?.data?.message || 'failed'}`);
        }
      }
      load(currentYear);
      setImportResult({ success, errors });
    } catch {
      setImportResult({ success: 0, errors: ['Failed to read file'] });
    } finally {
      setImportLoading(false);
    }
  };

  const usedNames = entries.map((e) => e.fee_name);
  const availableOptions = HOSTEL_FEE_OPTIONS.filter((o) => !usedNames.includes(o));

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newName) return;
    setLoading(true);
    setError(null);
    try {
      await hostelFeeTemplatesAPI.create(currentYear, newName, parseFloat(newTerm1) || 0, parseFloat(newTerm2) || 0);
      setNewName('');
      setNewTerm1('');
      setNewTerm2('');
      load(currentYear);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add hostel fee');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (entry) => {
    setEditId(entry.id);
    setEditTerm1(String(entry.fee_term1 || ''));
    setEditTerm2(String(entry.fee_term2 || ''));
  };

  const handleEditSave = async (id) => {
    setError(null);
    try {
      const entry = entries.find((e) => e.id === id);
      await hostelFeeTemplatesAPI.update(id, entry.fee_name, parseFloat(editTerm1) || 0, parseFloat(editTerm2) || 0);
      setEditId(null);
      load(currentYear);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update hostel fee');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this hostel fee entry?')) return;
    try {
      await hostelFeeTemplatesAPI.delete(id);
      load(currentYear);
    } catch { alert('Failed to remove hostel fee.'); }
  };

  return (
    <div className="fee-templates-section">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 700, margin: 0 }}>🏨 Hostel Fee Template — {currentYear}</h2>
        <input type="file" ref={hostelImportRef} accept=".csv" style={{ display: 'none' }} onChange={handleHostelImportCSV} />
        <button
          type="button"
          className="btn btn-sm"
          style={{ background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0' }}
          onClick={() => { setImportResult(null); hostelImportRef.current?.click(); }}
          disabled={importLoading}
        >
          {importLoading ? 'Importing…' : '📥 Import CSV'}
        </button>
        <a
          href="data:text/csv;charset=utf-8,fee_name%2Cterm1%2Cterm2"
          download="hostel_fee_template.csv"
          style={{ fontSize: '0.8rem', color: '#6b7280', textDecoration: 'underline' }}
        >
          Download template
        </a>
      </div>
      <p style={{ color: '#666', fontSize: '0.85rem', marginBottom: importResult ? 8 : 14 }}>
        Set default hostel facility amounts for <strong>{currentYear}</strong>. These auto-fill when a student opts in for hostel accommodation.
      </p>
      {importResult && (
        <div style={{ marginBottom: 10, padding: '8px 12px', borderRadius: 6, background: importResult.errors.length === 0 ? '#f0fdf4' : '#fff7ed', border: `1px solid ${importResult.errors.length === 0 ? '#bbf7d0' : '#fed7aa'}`, fontSize: '0.85rem' }}>
          {importResult.success > 0 && <div style={{ color: '#166534' }}>✅ Imported {importResult.success} row{importResult.success !== 1 ? 's' : ''}.</div>}
          {importResult.errors.map((e, i) => <div key={i} style={{ color: '#c2410c' }}>❌ {e}</div>)}
        </div>
      )}
      {error && <div className="error-banner" style={{ marginBottom: 10 }}>{error}</div>}
      {availableOptions.length > 0 && (
        <form onSubmit={handleAdd} style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <label style={{ fontSize: '.78rem', color: '#555' }}>Fee Type Name <span style={{ color: 'red' }}>*</span></label>
            <select
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, width: 180 }}
              required
            >
              <option value="">Select type…</option>
              {availableOptions.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <label style={{ fontSize: '.78rem', color: '#555' }}>Term 1 Fee (₹)</label>
            <input
              type="number"
              value={newTerm1}
              onChange={(e) => setNewTerm1(e.target.value)}
              placeholder="e.g. 20000"
              style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, width: 110 }}
              min="0"
              step="1"
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <label style={{ fontSize: '.78rem', color: '#555' }}>Term 2 Fee (₹)</label>
            <input
              type="number"
              value={newTerm2}
              onChange={(e) => setNewTerm2(e.target.value)}
              placeholder="e.g. 20000"
              style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, width: 110 }}
              min="0"
              step="1"
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <label style={{ fontSize: '.78rem', color: '#999' }}>Total (₹/year)</label>
            <input
              type="text"
              value={(parseFloat(newTerm1) || 0) + (parseFloat(newTerm2) || 0) > 0
                ? ((parseFloat(newTerm1) || 0) + (parseFloat(newTerm2) || 0)).toLocaleString('en-IN')
                : '—'}
              readOnly
              style={{ padding: '6px 10px', border: '1px solid #eee', borderRadius: 6, width: 110, background: '#f8f8f8', color: '#555' }}
            />
          </div>
          <button type="submit" className="btn btn-primary btn-sm" disabled={loading} style={{ alignSelf: 'flex-end' }}>
            {loading ? 'Adding…' : '+ Add Hostel Fee'}
          </button>
        </form>
      )}
      {entries.length === 0 ? (
        <p style={{ color: '#999', fontSize: '0.85rem' }}>No hostel fee entries yet for {currentYear}.</p>
      ) : (
        <div className="table-container">
        <table className="data-table" style={{ maxWidth: 680 }}>
          <thead>
            <tr>
              <th>Fee Type Name</th>
              <th>Term 1 (₹)</th>
              <th>Term 2 (₹)</th>
              <th>Total (₹/year)</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <tr key={entry.id}>
                <td><strong>{entry.fee_name}</strong></td>
                <td>
                  {editId === entry.id
                    ? <input type="number" value={editTerm1} onChange={(e) => setEditTerm1(e.target.value)} style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: 5, width: 90 }} min="0" step="1" />
                    : entry.fee_term1 > 0 ? fmt(entry.fee_term1) : <span style={{ color: '#aaa' }}>—</span>}
                </td>
                <td>
                  {editId === entry.id
                    ? <input type="number" value={editTerm2} onChange={(e) => setEditTerm2(e.target.value)} style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: 5, width: 90 }} min="0" step="1" />
                    : entry.fee_term2 > 0 ? fmt(entry.fee_term2) : <span style={{ color: '#aaa' }}>—</span>}
                </td>
                <td>
                  {editId === entry.id
                    ? <span style={{ color: '#555', fontWeight: 600 }}>
                        {((parseFloat(editTerm1) || 0) + (parseFloat(editTerm2) || 0)).toLocaleString('en-IN') || '—'}
                      </span>
                    : entry.fee_amount > 0 ? fmt(entry.fee_amount) : <span style={{ color: '#aaa' }}>—</span>}
                </td>
                <td style={{ display: 'flex', gap: 4 }}>
                  {editId === entry.id ? (
                    <>
                      <button className="btn btn-sm btn-primary" onClick={() => handleEditSave(entry.id)}>✔ Save</button>
                      <button className="btn btn-sm btn-secondary" onClick={() => setEditId(null)}>✕</button>
                    </>
                  ) : (
                    <>
                      <button className="btn btn-sm btn-secondary" onClick={() => handleEdit(entry)} title="Edit">✏️</button>
                      <button className="btn btn-sm btn-danger" onClick={() => handleDelete(entry.id)} title="Remove">🗑️</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
}

// ── Transport Fee Templates Section ───────────────────────────────────────────
function TransportFeeSection() {
  const { year: currentYear } = useYear();
  const [entries,   setEntries]   = useState([]);
  const [newName,   setNewName]   = useState('');
  const [newTerm1,  setNewTerm1]  = useState('');
  const [newTerm2,  setNewTerm2]  = useState('');
  const [editId,    setEditId]    = useState(null);
  const [editTerm1, setEditTerm1] = useState('');
  const [editTerm2, setEditTerm2] = useState('');
  const [loading,         setLoading]         = useState(false);
  const [error,           setError]           = useState(null);
  const transportImportRef                    = useRef(null);
  const [importLoading,   setImportLoading]   = useState(false);
  const [importResult,    setImportResult]    = useState(null);

  const load = (yr) => {
    transportFeeTemplatesAPI.getAll(yr)
      .then((r) => setEntries(r.data.data || []))
      .catch(() => {});
  };

  useEffect(() => { if (currentYear) load(currentYear); }, [currentYear]);

  const handleTransportImportCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    setImportResult(null);
    setImportLoading(true);
    try {
      const text = await file.text();
      const lines = text.trim().split('\n').filter(Boolean);
      const dataLines = lines[0]?.toLowerCase().includes('village') ? lines.slice(1) : lines;
      let success = 0;
      const errors = [];
      for (const line of dataLines) {
        const parts = line.split(',').map((s) => s.trim().replace(/^"|"$/g, ''));
        const [village, t1, t2] = parts;
        if (!village) continue;
        try {
          await transportFeeTemplatesAPI.create(currentYear, village, parseFloat(t1) || 0, parseFloat(t2) || 0);
          success++;
        } catch (err) {
          errors.push(`"${village}": ${err.response?.data?.message || 'failed'}`);
        }
      }
      load(currentYear);
      setImportResult({ success, errors });
    } catch {
      setImportResult({ success: 0, errors: ['Failed to read file'] });
    } finally {
      setImportLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setLoading(true);
    setError(null);
    try {
      await transportFeeTemplatesAPI.create(currentYear, newName.trim(), parseFloat(newTerm1) || 0, parseFloat(newTerm2) || 0);
      setNewName('');
      setNewTerm1('');
      setNewTerm2('');
      load(currentYear);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add transport fee');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (entry) => {
    setEditId(entry.id);
    setEditTerm1(String(entry.fee_term1 || ''));
    setEditTerm2(String(entry.fee_term2 || ''));
  };

  const handleEditSave = async (id) => {
    setError(null);
    try {
      const entry = entries.find((e) => e.id === id);
      await transportFeeTemplatesAPI.update(id, entry.village_name, parseFloat(editTerm1) || 0, parseFloat(editTerm2) || 0);
      setEditId(null);
      load(currentYear);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update transport fee');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this transport fee entry?')) return;
    try {
      await transportFeeTemplatesAPI.delete(id);
      load(currentYear);
    } catch { alert('Failed to remove transport fee.'); }
  };

  return (
    <div className="fee-templates-section">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 700, margin: 0 }}>🚌 Transport Fee Template — {currentYear}</h2>
        <input type="file" ref={transportImportRef} accept=".csv" style={{ display: 'none' }} onChange={handleTransportImportCSV} />
        <button
          type="button"
          className="btn btn-sm"
          style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' }}
          onClick={() => { setImportResult(null); transportImportRef.current?.click(); }}
          disabled={importLoading}
        >
          {importLoading ? 'Importing…' : '📥 Import CSV'}
        </button>
        <a
          href="data:text/csv;charset=utf-8,village%2Cterm1%2Cterm2"
          download="transport_fee_template.csv"
          style={{ fontSize: '0.8rem', color: '#6b7280', textDecoration: 'underline' }}
        >
          Download template
        </a>
      </div>
      <p style={{ color: '#666', fontSize: '0.85rem', marginBottom: importResult ? 8 : 14 }}>
        Set default transport fee amounts for <strong>{currentYear}</strong>. These auto-fill when a student opts in for transport.
      </p>
      {importResult && (
        <div style={{ marginBottom: 10, padding: '8px 12px', borderRadius: 6, background: importResult.errors.length === 0 ? '#f0fdf4' : '#fff7ed', border: `1px solid ${importResult.errors.length === 0 ? '#bbf7d0' : '#fed7aa'}`, fontSize: '0.85rem' }}>
          {importResult.success > 0 && <div style={{ color: '#166534' }}>✅ Imported {importResult.success} row{importResult.success !== 1 ? 's' : ''}.</div>}
          {importResult.errors.map((e, i) => <div key={i} style={{ color: '#c2410c' }}>❌ {e}</div>)}
        </div>
      )}
      {error && <div className="error-banner" style={{ marginBottom: 10 }}>{error}</div>}
      <form onSubmit={handleAdd} style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <label style={{ fontSize: '.78rem', color: '#555' }}>Village <span style={{ color: 'red' }}>*</span></label>
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="e.g. Kondapur"
            style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, width: 180 }}
            required
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <label style={{ fontSize: '.78rem', color: '#555' }}>Term 1 Fee (₹)</label>
          <input
            type="number"
            value={newTerm1}
            onChange={(e) => setNewTerm1(e.target.value)}
            placeholder="e.g. 5000"
            style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, width: 110 }}
            min="0"
            step="1"
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <label style={{ fontSize: '.78rem', color: '#555' }}>Term 2 Fee (₹)</label>
          <input
            type="number"
            value={newTerm2}
            onChange={(e) => setNewTerm2(e.target.value)}
            placeholder="e.g. 5000"
            style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, width: 110 }}
            min="0"
            step="1"
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <label style={{ fontSize: '.78rem', color: '#999' }}>Total (₹/year)</label>
          <input
            type="text"
            value={(parseFloat(newTerm1) || 0) + (parseFloat(newTerm2) || 0) > 0
              ? ((parseFloat(newTerm1) || 0) + (parseFloat(newTerm2) || 0)).toLocaleString('en-IN')
              : '—'}
            readOnly
            style={{ padding: '6px 10px', border: '1px solid #eee', borderRadius: 6, width: 110, background: '#f8f8f8', color: '#555' }}
          />
        </div>
        <button type="submit" className="btn btn-primary btn-sm" disabled={loading} style={{ alignSelf: 'flex-end' }}>
          {loading ? 'Adding…' : '+ Add Transport Fee'}
        </button>
      </form>
      {entries.length === 0 ? (
        <p style={{ color: '#999', fontSize: '0.85rem' }}>No transport fee entries yet for {currentYear}.</p>
      ) : (
        <div className="table-container">
        <table className="data-table" style={{ maxWidth: 680 }}>
          <thead>
            <tr>
              <th>Village</th>
              <th>Term 1 (₹)</th>
              <th>Term 2 (₹)</th>
              <th>Total (₹/year)</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <tr key={entry.id}>
                <td><strong>{entry.village_name}</strong></td>
                <td>
                  {editId === entry.id
                    ? <input type="number" value={editTerm1} onChange={(e) => setEditTerm1(e.target.value)} style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: 5, width: 90 }} min="0" step="1" />
                    : entry.fee_term1 > 0 ? fmt(entry.fee_term1) : <span style={{ color: '#aaa' }}>—</span>}
                </td>
                <td>
                  {editId === entry.id
                    ? <input type="number" value={editTerm2} onChange={(e) => setEditTerm2(e.target.value)} style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: 5, width: 90 }} min="0" step="1" />
                    : entry.fee_term2 > 0 ? fmt(entry.fee_term2) : <span style={{ color: '#aaa' }}>—</span>}
                </td>
                <td>
                  {editId === entry.id
                    ? <span style={{ color: '#555', fontWeight: 600 }}>
                        {((parseFloat(editTerm1) || 0) + (parseFloat(editTerm2) || 0)).toLocaleString('en-IN') || '—'}
                      </span>
                    : entry.fee_amount > 0 ? fmt(entry.fee_amount) : <span style={{ color: '#aaa' }}>—</span>}
                </td>
                <td style={{ display: 'flex', gap: 4 }}>
                  {editId === entry.id ? (
                    <>
                      <button className="btn btn-sm btn-primary" onClick={() => handleEditSave(entry.id)}>✔ Save</button>
                      <button className="btn btn-sm btn-secondary" onClick={() => setEditId(null)}>✕</button>
                    </>
                  ) : (
                    <>
                      <button className="btn btn-sm btn-secondary" onClick={() => handleEdit(entry)} title="Edit">✏️</button>
                      <button className="btn btn-sm btn-danger" onClick={() => handleDelete(entry.id)} title="Remove">🗑️</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
}

// ── User Management Section ───────────────────────────────────────────────────
const ALL_TABS = [
  'dashboard', 'students', 'reports', 'daybook', 'exams', 'operations',
  'certificates', 'biometric', 'employees',
  'whatsapp', 'chat', 'messages', 'meeting',
];
const TAB_LABELS = {
  dashboard:    '📊 Dashboard',
  students:     '👨‍🎓 Students',
  reports:      '📈 Accounts',
  daybook:      '📒 Day Book',
  exams:        '📝 Exams',
  operations:   '⚙️ Operations',
  certificates: '🎖️ Certificates',
  biometric:    '🔍 Biometric Verification',
  employees:    '💼 Employees',
  whatsapp:     '💚 WhatsApp',
  chat:         '💬 Messages',
  messages:     '📨 Bulk Messages',
  meeting:      '📹 Meeting',
};
const emptyUser = () => ({ username: '', password: '', full_name: '', email: '', allowed_tabs: ['students'] });

function TabCheckboxes({ value, onChange, compact }) {
  const toggle = (tab) => {
    const next = value.includes(tab) ? value.filter((t) => t !== tab) : [...value, tab];
    onChange(next);
  };
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: compact ? 4 : 8 }}>
      {ALL_TABS.map((tab) => (
        <label key={tab} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: '0.82rem', padding: compact ? '2px 6px' : '4px 8px', borderRadius: 6, border: `1px solid ${value.includes(tab) ? '#3b82f6' : '#d1d5db'}`, background: value.includes(tab) ? '#eff6ff' : '#f9fafb', fontWeight: value.includes(tab) ? 600 : 400, userSelect: 'none' }}>
          <input type="checkbox" checked={value.includes(tab)} onChange={() => toggle(tab)} style={{ width: 13, height: 13 }} />
          {TAB_LABELS[tab]}
        </label>
      ))}
    </div>
  );
}

function UserManagementSection() {
  const stored = sessionStorage.getItem('gs_allowed_tabs');
  // If no tabs stored (old session / main admin), treat as all-access
  const allowedTabs = stored
    ? (() => { try { return JSON.parse(stored); } catch { return ALL_TABS; } })()
    : ALL_TABS;

  const [users,       setUsers]       = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState(null);
  const [showForm,    setShowForm]    = useState(false);
  const [form,        setForm]        = useState(emptyUser());
  const [saving,      setSaving]      = useState(false);
  const [editId,      setEditId]      = useState(null);
  const [editData,    setEditData]    = useState({});
  const [resetId,     setResetId]     = useState(null);
  const [resetPw,     setResetPw]     = useState('');
  const [resetSaving, setResetSaving] = useState(false);
  const [msg,         setMsg]         = useState(null);
  const [employees,   setEmployees]   = useState([]);

  const load = () => {
    setLoading(true);
    usersAPI.getAll()
      .then((r) => setUsers(r.data.data || []))
      .catch(() => setError('Failed to load users'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    employeesAPI.getAll()
      .then((r) => setEmployees((r.data.data || []).filter((e) => e.is_active)))
      .catch(() => {});
  }, []); // eslint-disable-line

  // Only school owner (has both operations + dashboard) sees this
  if (!allowedTabs.includes('operations') || !allowedTabs.includes('dashboard')) return null;

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(null), 3000); };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (form.allowed_tabs.length === 0) { alert('Select at least one tab'); return; }
    setSaving(true);
    try {
      await usersAPI.create({ username: form.username, password: form.password, full_name: form.full_name, email: form.email, allowed_tabs: form.allowed_tabs });
      setForm(emptyUser());
      setShowForm(false);
      flash('✅ User created');
      load();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to create user');
    } finally { setSaving(false); }
  };

  const handleSaveEdit = async (id) => {
    if (editData.allowed_tabs && editData.allowed_tabs.length === 0) { alert('Select at least one tab'); return; }
    try {
      await usersAPI.update(id, editData);
      setEditId(null);
      flash('✅ User updated');
      load();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update');
    }
  };

  const handleResetPassword = async (id) => {
    if (!resetPw || resetPw.length < 4) { alert('Password must be at least 4 characters'); return; }
    setResetSaving(true);
    try {
      await usersAPI.update(id, { password: resetPw });
      setResetId(null);
      setResetPw('');
      flash('✅ Password reset');
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to reset password');
    } finally { setResetSaving(false); }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Delete user "${u.username}"?\nThis cannot be undone.`)) return;
    try {
      await usersAPI.delete(u.user_id);
      flash('✅ User deleted');
      load();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete user');
    }
  };

  return (
    <div className="fee-templates-section" style={{ marginTop: 32 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 700, margin: 0 }}>👥 Staff Accounts & Permissions</h2>
        <button className="btn btn-primary btn-sm" onClick={() => { setShowForm((v) => !v); setForm(emptyUser()); }}>
          {showForm ? 'Cancel' : '+ Add Staff Account'}
        </button>
      </div>
      <p style={{ color: '#666', fontSize: '0.85rem', marginBottom: 14 }}>
        Create logins for your staff. Choose exactly which tabs each person can see.
        Only the school owner sees <strong>Dashboard</strong> and <strong>Operations</strong>.
      </p>

      {msg   && <div className="success-banner" style={{ marginBottom: 10 }}>{msg}</div>}
      {error && <p style={{ color: '#dc2626' }}>{error}</p>}

      {/* ── Add staff form ── */}
      {showForm && (
        <form onSubmit={handleCreate} style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '16px 20px', marginBottom: 20 }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151' }}>Username *</label>
              <input required value={form.username} onChange={(e) => setForm((p) => ({ ...p, username: e.target.value }))}
                placeholder="e.g. ramesh" style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, width: 150, fontSize: '0.9rem' }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151' }}>Password *</label>
              <input required type="password" value={form.password} onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                placeholder="min 4 chars" style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, width: 150, fontSize: '0.9rem' }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151' }}>Full Name *</label>
              <select required value={form.full_name} onChange={(e) => setForm((p) => ({ ...p, full_name: e.target.value }))}
                style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, width: 190, fontSize: '0.9rem', background: '#fff' }}>
                <option value="">— Select Employee —</option>
                {employees.map((emp) => (
                  <option key={emp.employee_id} value={emp.full_name}>{emp.full_name}{emp.designation ? ` (${emp.designation})` : ''}</option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151' }}>Email *</label>
              <input required type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                placeholder="staff@school.com" style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, width: 200, fontSize: '0.9rem' }} />
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151', marginBottom: 6 }}>Tabs this user can access *</div>
            <TabCheckboxes value={form.allowed_tabs} onChange={(tabs) => setForm((p) => ({ ...p, allowed_tabs: tabs }))} />
          </div>
          <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>{saving ? 'Creating…' : '✅ Create Account'}</button>
        </form>
      )}

      {/* ── Users list ── */}
      {loading ? (
        <p style={{ color: '#999' }}>Loading…</p>
      ) : users.length === 0 ? (
        <p style={{ color: '#999', fontSize: '0.85rem' }}>No staff accounts yet. Click <strong>+ Add Staff Account</strong> to create one.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {users.map((u) => (
            <div key={u.user_id} style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 16px', background: u.is_active ? '#fff' : '#fafafa', opacity: u.is_active ? 1 : 0.7 }}>
              {editId === u.user_id ? (
                /* ── Edit mode ── */
                <div>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151' }}>Full Name</label>
                      <input value={editData.full_name || ''} onChange={(e) => setEditData((p) => ({ ...p, full_name: e.target.value }))}
                        style={{ padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 6, width: 170, fontSize: '0.9rem' }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151' }}>Email *</label>
                      <input required type="email" value={editData.email || ''} onChange={(e) => setEditData((p) => ({ ...p, email: e.target.value }))}
                        style={{ padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 6, width: 200, fontSize: '0.9rem' }} />
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', cursor: 'pointer' }}>
                      <input type="checkbox" checked={editData.is_active !== undefined ? editData.is_active : !!u.is_active}
                        onChange={(e) => setEditData((p) => ({ ...p, is_active: e.target.checked }))} style={{ width: 15, height: 15 }} />
                      Active
                    </label>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151', marginBottom: 6 }}>Allowed Tabs</div>
                    <TabCheckboxes value={editData.allowed_tabs || u.allowed_tabs || []} onChange={(tabs) => setEditData((p) => ({ ...p, allowed_tabs: tabs }))} />
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-primary btn-sm" onClick={() => handleSaveEdit(u.user_id)}>💾 Save</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => setEditId(null)}>Cancel</button>
                  </div>
                </div>
              ) : resetId === u.user_id ? (
                /* ── Reset password mode ── */
                <div>
                  <div style={{ fontWeight: 600, marginBottom: 10 }}>🔑 Reset password for <em>{u.username}</em></div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <input type="password" value={resetPw} onChange={(e) => setResetPw(e.target.value)}
                      placeholder="New password (min 4 chars)"
                      style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, width: 220, fontSize: '0.9rem' }} />
                    <button className="btn btn-primary btn-sm" disabled={resetSaving} onClick={() => handleResetPassword(u.user_id)}>
                      {resetSaving ? 'Saving…' : '✅ Set Password'}
                    </button>
                    <button className="btn btn-secondary btn-sm" onClick={() => { setResetId(null); setResetPw(''); }}>Cancel</button>
                  </div>
                </div>
              ) : (
                /* ── View mode ── */
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                    <div>
                      <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>{u.username}</span>
                      {u.full_name && <span style={{ color: '#6b7280', marginLeft: 8, fontSize: '0.88rem' }}>{u.full_name}</span>}
                      {u.email && <span style={{ color: '#6b7280', marginLeft: 8, fontSize: '0.82rem' }}>· {u.email}</span>}
                      {!u.is_active && <span style={{ marginLeft: 8, fontSize: '0.75rem', color: '#ef4444', fontWeight: 600 }}>INACTIVE</span>}
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => { setEditId(u.user_id); setEditData({ full_name: u.full_name || '', email: u.email || '', allowed_tabs: u.allowed_tabs ? [...u.allowed_tabs] : [], is_active: !!u.is_active }); }}>✏️ Edit</button>
                      <button className="btn btn-sm" style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d' }} onClick={() => { setResetId(u.user_id); setResetPw(''); }}>🔑 Reset Password</button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(u)}>🗑️ Delete</button>
                    </div>
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: 4 }}>Can access:</div>
                    <TabCheckboxes value={u.allowed_tabs || []} onChange={() => {}} compact />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Monthly Fee Settings Card ─────────────────────────────────────────────────
function MonthlySettingsCard() {
  const [months,  setMonths]  = useState('');
  const [saved,   setSaved]   = useState(false);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    settingsAPI.getAll()
      .then((r) => {
        const val = r.data.data?.monthly_months;
        setMonths(val !== undefined ? String(val) : '11');
      })
      .catch(() => setMonths('11'));
  }, []);

  const handleSave = async () => {
    const n = parseInt(months, 10);
    if (isNaN(n) || n < 1 || n > 12) {
      setError('Enter a number between 1 and 12');
      return;
    }
    setLoading(true);
    setError(null);
    setSaved(false);
    try {
      await settingsAPI.set('monthly_months', n);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ops-section">
      <div className="ops-section-header">
        <div className="ops-section-icon">📅</div>
        <span className="ops-section-title">Monthly Fee Settings</span>
        <span className="ops-section-sub">Configure how monthly instalment is calculated</span>
      </div>
      <div className="ops-grid" style={{ gridTemplateColumns: '1fr' }}>
        <div className="ops-card blue" style={{ maxWidth: 420 }}>
          <div className="ops-card-icon">🗓️</div>
          <div className="ops-card-title">Number of Monthly Instalments</div>
          <div className="ops-card-desc" style={{ marginBottom: 12 }}>
            When a payment is recorded as <strong>Monthly Tuition</strong>, the amount field will
            auto-fill as <strong>Total Fee ÷ this number</strong>.
          </div>
          {error  && <div className="error-banner" style={{ marginBottom: 8 }}>{error}</div>}
          {saved  && <div className="success-banner" style={{ marginBottom: 8 }}>✅ Saved successfully</div>}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <input
              type="number"
              min="1"
              max="12"
              value={months}
              onChange={(e) => { setMonths(e.target.value); setSaved(false); setError(null); }}
              style={{
                width: 80, padding: '6px 10px', borderRadius: 6,
                border: '1.5px solid #c7d2fe', fontSize: 15, fontWeight: 700,
                textAlign: 'center',
              }}
            />
            <span style={{ color: '#555', fontSize: 13 }}>months per year (1–12)</span>
          </div>
          <div className="ops-card-footer" style={{ marginTop: 14 }}>
            <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={loading}>
              {loading ? 'Saving…' : 'Save Setting'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Message Templates Section ─────────────────────────────────────────────────
// Lets school admins manage their own reusable message templates that appear
// in both the Messages composer (Chat) and WhatsApp Bulk page.
function MessageTemplatesSection() {
  const stored = sessionStorage.getItem('gs_allowed_tabs');
  const allowedTabs = stored
    ? (() => { try { return JSON.parse(stored); } catch { return null; } })() || ['operations', 'dashboard']
    : ['operations', 'dashboard'];
  const isAdmin = allowedTabs.includes('operations') && allowedTabs.includes('dashboard');

  const [list,    setList]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [editing, setEditing] = useState(null);   // template row being edited, or { __new: true }

  const load = () => {
    setLoading(true);
    setError('');
    messageTemplatesAPI.getAll()
      .then((r) => setList(r.data?.data || []))
      .catch((err) => setError(err.response?.data?.message || 'Failed to load templates'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  if (!isAdmin) return null;

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete template "${row.name}"? This cannot be undone.`)) return;
    try {
      await messageTemplatesAPI.delete(row.template_id);
      load();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete');
    }
  };

  return (
    <div className="fee-templates-section" style={{ marginTop: 32 }}>
      <div className="section-header">
        <h2 style={{ margin: 0, fontSize: 20, color: '#1a237e' }}>📋 Message Templates</h2>
        <button className="btn btn-primary btn-sm" onClick={() => setEditing({ __new: true, name: '', category: 'custom', description: '', body: '' })}>
          + New Template
        </button>
      </div>
      <p style={{ color: '#555', marginTop: 4, marginBottom: 14, fontSize: 13 }}>
        Save reusable message templates that appear inside the <b>Messages</b> composer and
        the <b>WhatsApp Bulk</b> page. Templates are shared with all staff at this school.
      </p>

      {error   && <div className="error-banner">{error}</div>}
      {loading && <div style={{ padding: 12, color: '#6b7280', fontSize: 13 }}>Loading…</div>}

      {!loading && list.length === 0 && (
        <div style={{
          padding: 18, background: '#f8fafc', border: '1px dashed #cbd5e1',
          borderRadius: 8, color: '#6b7280', fontSize: 13, textAlign: 'center',
        }}>
          No custom templates yet. Click <b>+ New Template</b> to create one.
        </div>
      )}

      {!loading && list.length > 0 && (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Category</th>
                <th style={{ minWidth: 280 }}>Body (preview)</th>
                <th style={{ width: 160 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((row) => {
                const cat = TEMPLATE_CATEGORIES.find((c) => c.key === row.category);
                return (
                  <tr key={row.template_id}>
                    <td><b>{row.name}</b>{row.description ? <div style={{ fontSize: 11.5, color: '#6b7280' }}>{row.description}</div> : null}</td>
                    <td>{cat ? cat.label : row.category}</td>
                    <td style={{ fontSize: 12, color: '#374151', whiteSpace: 'pre-wrap' }}>
                      {(row.body || '').slice(0, 160)}{(row.body || '').length > 160 ? '…' : ''}
                    </td>
                    <td>
                      <button className="btn btn-secondary btn-sm" onClick={() => setEditing(row)}>Edit</button>
                      <button className="btn btn-danger btn-sm" style={{ marginLeft: 6 }} onClick={() => handleDelete(row)}>Delete</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <MessageTemplateEditor
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

// Modal form for create / edit
function MessageTemplateEditor({ initial, onClose, onSaved }) {
  const isNew = !!initial.__new;
  const [form, setForm] = useState({
    name:        initial.name        || '',
    category:    initial.category    || 'custom',
    description: initial.description || '',
    body:        initial.body        || '',
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  const handleSave = async () => {
    if (!form.name.trim()) return setError('Name is required');
    if (!form.body.trim()) return setError('Body is required');
    setSaving(true);
    setError('');
    try {
      if (isNew) await messageTemplatesAPI.create(form);
      else       await messageTemplatesAPI.update(initial.template_id, form);
      onSaved();
    } catch (err) {
      setError(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-box large">
        <div className="modal-header">
          <h2>{isNew ? '➕ New Message Template' : '✏️ Edit Template'}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {error && <div className="error-banner">{error}</div>}
          <div className="form-grid" style={{ gridTemplateColumns: '2fr 1fr' }}>
            <div className="form-group">
              <label>Name <span className="required">*</span></label>
              <input
                type="text"
                value={form.name}
                maxLength={150}
                placeholder="e.g. Annual Day Invitation 2026"
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label>Category</label>
              <select value={form.category} onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}>
                {TEMPLATE_CATEGORIES.filter((c) => c.key !== 'custom').map((c) => (
                  <option key={c.key} value={c.key}>{c.label}</option>
                ))}
                <option value="custom">⭐ My Templates</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label>Short Description</label>
            <input
              type="text"
              value={form.description}
              maxLength={300}
              placeholder="One-liner shown to staff when picking the template"
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            />
          </div>
          <div className="form-group">
            <label>Body <span className="required">*</span></label>
            <textarea
              rows={10}
              value={form.body}
              maxLength={4000}
              placeholder={'Type the message body. Example:\n\nDear Parent,\n\nThe school will remain closed on 1st May 2026.\n\nThank you,\nSchool Management'}
              onChange={(e) => setForm((p) => ({ ...p, body: e.target.value }))}
              style={{ fontFamily: 'inherit', whiteSpace: 'pre-wrap' }}
            />
            <small style={{ color: '#6b7280', fontSize: 11.5 }}>
              {form.body.length} / 4000 characters
            </small>
          </div>
        </div>
        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : (isNew ? 'Create Template' : 'Save Changes')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── OperationsPanel ────────────────────────────────────────────────────────────
// ── Bulk Operations Section ────────────────────────────────────────────────────
function BulkOperationsSection() {
  const { year }            = useYear();
  const [classes, setClasses] = useState([]);
  const [modal,   setModal]   = useState(null); // 'promote' | 'bulkfee' | 'promote-individual' | null

  useEffect(() => {
    if (modal) window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [modal]);

  useEffect(() => {
    if (!year) return;
    studentsAPI.getClasses(year).then((r) => setClasses(r.data.data)).catch(() => {});
  }, [year]);

  return (
    <>
      <div className="ops-section">
        <div className="ops-section-header">
          <div className="ops-section-icon">⚡</div>
          <span className="ops-section-title">Bulk Operations</span>
          <span className="ops-section-sub">Year-end management tools</span>
        </div>
        <div className="ops-grid">
          <div className="ops-card blue">
            <div className="ops-card-icon">💰</div>
            <div className="ops-card-title">Bulk Fee Assignment</div>
            <div className="ops-card-desc">
              Set the same total fee for every student in a selected class at once for <strong>{year}</strong>. Saves time during fee structure setup.
            </div>
            <div className="ops-card-footer">
              <button className="btn btn-primary btn-sm" onClick={() => setModal('bulkfee')}>Open Tool →</button>
            </div>
          </div>
          <div className="ops-card green">
            <div className="ops-card-icon">📤</div>
            <div className="ops-card-title">Promote Students</div>
            <div className="ops-card-desc">
              Move all active students from <strong>{year}</strong> to the next class. Pending dues are automatically carried forward as Old Due.
            </div>
            <div className="ops-card-footer">
              <button className="btn btn-success btn-sm" onClick={() => setModal('promote')}>Open Tool →</button>
            </div>
          </div>
          <div className="ops-card" style={{ borderLeft: '4px solid #7c3aed' }}>
            <div className="ops-card-icon">👤</div>
            <div className="ops-card-title">Promote Individual Student</div>
            <div className="ops-card-desc">
              Search for a specific student and promote them individually to a new class and year. Use this when a single student needs to be moved separately from the class.
            </div>
            <div className="ops-card-footer">
              <button className="btn btn-sm" style={{ background: '#7c3aed', color: '#fff' }} onClick={() => setModal('promote-individual')}>Open Tool →</button>
            </div>
          </div>
        </div>
      </div>

      {modal === 'bulkfee' && (
        <BulkFeeModal classes={classes} currentYear={year} onClose={() => setModal(null)} />
      )}
      {modal === 'promote' && (
        <PromoteModal classes={classes} currentYear={year} onClose={() => setModal(null)} />
      )}
      {modal === 'promote-individual' && (
        <IndividualPromoteModal currentYear={year} onClose={() => setModal(null)} />
      )}
    </>
  );
}

// ── Fee Manage (combined page: all 4 fee sections on one screen) ─────────────
function FeeManageSection() {
  return (
    <>
      <MonthlySettingsCard />
      <hr className="section-divider" />
      <HostelFeeSection />
      <hr className="section-divider" />
      <TransportFeeSection />
      <hr className="section-divider" />
      <FeeTypesSection />
      <hr className="section-divider" />
      <FeeTemplatesSection />
    </>
  );
}

// ── Section registry (shared with sidebar nav in App.js) ───────────────────────
export const OPERATIONS_SECTIONS = [
  { key: 'fee-manage',     label: 'Fee Management',    description: 'Monthly, hostel, types & templates \u2014 all fee setup in one place' },
  { key: 'fee-templates',  label: 'Fee Templates',     description: 'Class-wise fee structures' },
  { key: 'monthly',        label: 'Monthly Settings',  description: 'Configure monthly fee schedule' },
  { key: 'hostel',         label: 'Hostel Fees',       description: 'Hostel fee templates' },
  { key: 'transport',      label: 'Transport Fee',     description: 'Village-wise transport fee templates' },
  { key: 'fee-types',      label: 'Fee Types',         description: 'Define fee categories' },
  { key: 'bulk',           label: 'Promote Students',  description: 'Year-end promotion & bulk fee tools' },
  { key: 'users',          label: 'User Management',   description: 'Staff accounts & access' },
  { key: 'messages',       label: 'Message Templates', description: 'WhatsApp / SMS templates' },
];

const SECTION_COMPONENTS = {
  'fee-manage':    FeeManageSection,
  bulk:            BulkOperationsSection,
  monthly:         MonthlySettingsCard,
  'fee-templates': FeeTemplatesSection,
  'fee-types':     FeeTypesSection,
  hostel:          HostelFeeSection,
  transport:       TransportFeeSection,
  users:           UserManagementSection,
  messages:        MessageTemplatesSection,
};

export default function OperationsPanel({ section }) {
  const { year } = useYear();
  const meta     = OPERATIONS_SECTIONS.find((s) => s.key === section);
  const Active   = meta ? SECTION_COMPONENTS[meta.key] : null;

  return (
    <div className="page">
      {/* ── Page Header ── */}
      <div className="page-header" style={{ marginBottom: '1.5rem' }}>
        <div>
          <h1 className="page-title">{meta ? meta.label : 'Operations'}</h1>
          <p style={{ color: 'var(--slate-500)', fontSize: '.85rem', marginTop: '.2rem' }}>
            {meta ? meta.description : 'Year-end & bulk operations'} · Academic year{' '}
            <strong style={{ color: 'var(--indigo-600)' }}>{year}</strong>
          </p>
        </div>
      </div>

      {Active ? (
        <Active />
      ) : (
        <div className="ops-section">
          <div className="ops-section-header">
            <div className="ops-section-icon">⚙️</div>
            <span className="ops-section-title">Pick a section</span>
            <span className="ops-section-sub">Use the sidebar to open a tool</span>
          </div>
          <div className="ops-grid">
            {OPERATIONS_SECTIONS.map((s) => (
              <div key={s.key} className="ops-card blue">
                <div className="ops-card-title">{s.label}</div>
                <div className="ops-card-desc">{s.description}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── ASR InfoTech Branding ── */}
      <div className="asr-branding" style={{ marginTop: '2rem' }}>
        Powered by <a href="https://asrinfotech.in" target="_blank" rel="noopener noreferrer">ASR InfoTech</a> · School Management Platform v3.3
      </div>
    </div>
  );
}
